import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

import Navbar from "../SharedComponents/Navbar";
import Footer from "../SharedComponents/Footer";
import CommentSkeleton from "../SharedComponents/CommentSkeleton";
import NoImage from "../assets/images/no-image-icon-23485.png";
import { UserCommentCard, UserCommentReview } from "../SharedComponents/UserComments";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const PAGE_SIZE = 12;

class ReviewsRequestError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = "ReviewsRequestError";
  }
}

interface PlaceDetails {
  name?: string;
  cities?: Array<{ name?: string }>;
  city?: string | { name?: string };
  images?: Array<{ file_url?: string }>;
}

const placeDetailsCache = new Map<string, Promise<PlaceDetails | null>>();

const getImageUrl = (fileUrl?: string | null) => {
  if (!fileUrl) return NoImage;
  if (/^https?:\/\//i.test(fileUrl)) return fileUrl;
  return `${BASE_URL ?? ""}${fileUrl.startsWith("/") ? "" : "/"}${fileUrl}`;
};

const fetchPlaceDetails = (placeId: string): Promise<PlaceDetails | null> => {
  const cachedRequest = placeDetailsCache.get(placeId);
  if (cachedRequest) return cachedRequest;

  const request = fetch(`${BASE_URL}/api/places/${placeId}/`)
    .then(async (response) => {
      if (!response.ok) throw new Error("Failed to fetch reviewed place");
      const body = await response.json();
      return body.data ?? body;
    })
    .catch(() => null);

  placeDetailsCache.set(placeId, request);
  return request;
};

const fetchReviewsPage = async (page: number) => {
  const response = await fetch(`${BASE_URL}/api/reviews/?page=${page}&page_size=${PAGE_SIZE}`);
  if (!response.ok) {
    if (response.status === 429) {
      throw new ReviewsRequestError(429, "درخواست‌های دریافت نظر موقتاً محدود شده‌اند. لطفاً چند لحظه دیگر تلاش کنید.");
    }
    throw new ReviewsRequestError(response.status, "دریافت نظرات کاربران ناموفق بود.");
  }

  const body = await response.json();
  const rawReviews = Array.isArray(body) ? body : body.results ?? body.data ?? [];
  const totalCount = Array.isArray(body) ? body.length : body.count ?? body.pagination?.count ?? rawReviews.length;

  const reviews = await Promise.all(
    rawReviews.map(async (review: any): Promise<UserCommentReview> => {
      const rawPlace = review.place;
      const placeId = String(
        typeof rawPlace === "object" && rawPlace !== null
          ? rawPlace.place_id ?? rawPlace.id ?? ""
          : rawPlace ?? "",
      );
      const place = placeId ? await fetchPlaceDetails(placeId) : null;
      const city = place?.cities?.[0]?.name ||
        (typeof place?.city === "string" ? place.city : place?.city?.name) ||
        review.city_name ||
        "شهر نامشخص";

      return {
        id: review.review_id || `${placeId}-${review.date}`,
        name: review.user_name === "Unknown" ? "کاربر سایت" : review.user_name || "کاربر",
        date: review.date || "",
        rate: review.rate || 0,
        comment: review.comment || "کاربر تجربه خود را ثبت کرده است.",
        place: place?.name || review.place_name || rawPlace?.name || "مکان نامشخص",
        city,
        img: getImageUrl(place?.images?.[0]?.file_url),
        placeId,
      };
    }),
  );

  return { reviews, totalCount };
};

const AllUserReviewsPage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["all-user-reviews", currentPage],
    queryFn: () => fetchReviewsPage(currentPage),
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  });

  const reviews = data?.reviews || [];
  const totalPages = Math.max(1, Math.ceil((data?.totalCount || 0) / PAGE_SIZE));

  const changePage = (nextPage: number) => {
    if (nextPage < 1 || nextPage > totalPages) return;
    setCurrentPage(nextPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="flex min-h-screen flex-col bg-white" dir="rtl">
      <Navbar />
      <main className="site-container flex-1 pb-10 pt-20 tablet:py-10">
        <header className="mb-6 flex items-baseline gap-2 border-b border-gray-200 pb-4">
          <h1 className="text-xl font-myYekanDemibold text-[#222222] tablet:text-2xl">نظرات کاربران</h1>
          {!isLoading && <span className="text-xs font-myYekanFaNumRegular text-gray-500">({data?.totalCount || 0})</span>}
        </header>

        {isLoading ? (
          <div className="grid justify-items-center gap-4 tablet:grid-cols-2 desktop:grid-cols-3">
            {Array.from({ length: 6 }).map((_, index) => <CommentSkeleton key={index} />)}
          </div>
        ) : isError ? (
          <div className="flex flex-col items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-5 text-sm text-red-700">
            <span>{(error as Error).message}</span>
            <button
              type="button"
              onClick={() => void refetch()}
              className="rounded-lg border border-red-200 bg-white px-3 py-2 text-xs font-myYekanMedium text-red-700 transition hover:bg-red-100"
            >
              تلاش دوباره
            </button>
          </div>
        ) : reviews.length ? (
          <>
            <div className="grid justify-items-center gap-4 tablet:grid-cols-2 desktop:grid-cols-3">
              {reviews.map((review) => <UserCommentCard key={review.id} review={review} />)}
            </div>

            {totalPages > 1 && (
              <nav className="mt-8 flex items-center justify-center gap-2" aria-label="صفحه‌بندی نظرات کاربران">
                <button
                  type="button"
                  onClick={() => changePage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label="صفحه قبل"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
                <span className="text-sm font-myYekanFaNumMedium text-gray-700">{currentPage} از {totalPages}</span>
                <button
                  type="button"
                  onClick={() => changePage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label="صفحه بعد"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
              </nav>
            )}
          </>
        ) : (
          <div className="rounded-lg border border-dashed border-gray-300 bg-[#FAFAFA] px-5 py-12 text-center text-sm text-gray-500">
            هنوز نظری ثبت نشده است.
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default AllUserReviewsPage;
